import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, Box, Typography, Slider, IconButton, CircularProgress, Button, Skeleton } from '@mui/material';
import {
    AccountBalance as AccountBalanceIcon,
    Smartphone,
    Business,
    Language,
    CreditCard,
    Add,
    Close,
    AccountBalanceWallet,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getUserExternalAccounts } from '../../redux/account/actions';
import { RootState } from '../../redux/root-reducer';
import { IExternalAccount } from '../../redux/account/types';
import { formatCurrency, getCurrencySymbol } from '../../constants/currency';
import { navigate } from '../../redux/navigator/action';
import { ROUTES } from '../../types/global/routes.types';
import { requestPayout } from '../../redux/requests/action';
import { toggleSubscriptionModal } from '../../redux/appConfigurator/action';
import type { TRequestStatus } from '../../redux/requests/types';
import {
    accountBalanceUrl,
    campaignAccountPayoutInfoUrl,
    creatorAccountPayoutInfoUrl,
    sandboxAccountPayoutInfoUrl,
    myPayoutsUrl,
} from '../../configurations/api/url';
import {
    formatCampaignScheduledPayout,
    normalizeCampaignPayoutInfo,
    type ICampaignPayoutInfo,
} from '../../types/global/campaignPayout.types';
import { normalizeAccountPayoutInfo, type IAccountPayoutInfo } from '../../types/global/accountPayout.types';

interface IPayoutRecord {
    _id: string;
    payout_amount: number; // cents
    request_status: TRequestStatus;
    request_kind?: string;
    createdAt: string;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const BG = '#0e0e0e';
const SURFACE = '#161616';
const CARD = '#1e1e1e';
const BORDER = 'rgba(255,255,255,0.07)';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const RED = '#ff6b6b';
const AMBER = '#ffb547';
const MUTED = '#7a7a7a';
const WHITE = '#f5f5f5';
// ── Helpers ───────────────────────────────────────────────────────────────────
function statusMeta(s: TRequestStatus): { label: string; color: string; bg: string } {
    switch (s) {
        case 'approved':
            return { label: 'Paid out', color: MINT, bg: 'rgba(0,253,193,0.1)' };
        case 'denied':
            return { label: 'Declined', color: RED, bg: 'rgba(255,107,107,0.1)' };
        case 'action requested':
            return { label: 'Action needed', color: AMBER, bg: 'rgba(255,181,71,0.1)' };
        default:
            return { label: 'Under review', color: GOLD, bg: 'rgba(255,201,101,0.1)' };
    }
}

function accountIcon(type: string) {
    const sx = { fontSize: 18, color: GOLD };
    switch (type) {
        case 'nuban':
            return <CreditCard sx={sx} />;
        case 'mobile_money':
            return <Smartphone sx={sx} />;
        case 'bank_account':
            return <AccountBalanceIcon sx={sx} />;
        case 'ach':
            return <AccountBalanceIcon sx={sx} />;
        case 'paypal':
            return <Business sx={sx} />;
        case 'swift':
            return <Language sx={sx} />;
        default:
            return <AccountBalanceIcon sx={sx} />;
    }
}

function accountLabel(acc: IExternalAccount): string {
    switch (acc.external_account_type) {
        case 'nuban':
            return `${acc.bank_details?.bank_name ?? 'Bank'} · ****${String(
                acc.bank_details?.account_number ?? '',
            ).slice(-4)}`;
        case 'mobile_money':
            return `${acc.mobile_money_details?.mobile_money_name ?? 'Mobile Money'} · ${
                acc.mobile_money_details?.phone_number ?? ''
            }`;
        case 'bank_account':
            return `Wire · ****${String(acc.bank_details?.account_number ?? '').slice(-4)}`;
        case 'ach':
            return `ACH · ****${String(acc.bank_details?.account_number ?? '').slice(-4)}`;
        case 'paypal':
            return `PayPal · ${acc.paypal_details?.email ?? ''}`;
        default:
            return acc.name ?? 'Account';
    }
}

function accountId(acc: IExternalAccount): string {
    return String(acc._id ?? acc.id ?? '');
}

function ErrorBanner({ msg, amber }: { msg: string; amber?: boolean }) {
    return (
        <Box
            sx={{
                bgcolor: amber ? 'rgba(255,181,71,0.08)' : 'rgba(255,107,107,0.08)',
                border: `1px solid ${amber ? 'rgba(255,181,71,0.2)' : 'rgba(255,107,107,0.2)'}`,
                borderRadius: 2,
                px: 2,
                py: 1.25,
            }}>
            <Typography sx={{ fontSize: 13, color: amber ? AMBER : RED, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                {msg}
            </Typography>
        </Box>
    );
}

/** Unsubscribed / scheduled-only gate — no early Request Payout submit. */
function ScheduledPayoutGate({
    message,
    scheduledLabel,
    onSubscribe,
    ctaLabel = 'Subscribe for early cash out',
    title = 'Early cash out is for subscribers',
}: {
    message: string;
    scheduledLabel: string;
    onSubscribe: () => void;
    ctaLabel?: string;
    title?: string;
}) {
    return (
        <Box
            sx={{
                bgcolor: CARD,
                borderRadius: 3,
                px: 2.5,
                py: 2.5,
                border: '1px solid rgba(255,181,71,0.25)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}>
            <Box>
                <Typography sx={{ fontSize: 15, fontFamily: FONT_ENUM.MAIN_BOLD, color: WHITE, mb: 0.75 }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 13, color: MUTED, fontFamily: FONT_ENUM.MAIN_MEDIUM, lineHeight: 1.5 }}>
                    {message}
                </Typography>
            </Box>
            {scheduledLabel ? (
                <Box
                    sx={{
                        bgcolor: SURFACE,
                        borderRadius: 2,
                        px: 2,
                        py: 1.5,
                        border: `1px solid ${BORDER}`,
                    }}>
                    <Typography
                        sx={{
                            fontSize: 10,
                            color: MUTED,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            letterSpacing: '0.1em',
                            mb: 0.5,
                        }}>
                        NEXT AUTOMATIC PAYOUT
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontFamily: FONT_ENUM.MAIN_BOLD, color: AMBER }}>
                        {scheduledLabel}
                    </Typography>
                </Box>
            ) : null}
            <Button
                fullWidth
                onClick={onSubscribe}
                sx={{
                    py: 1.5,
                    borderRadius: 999,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 14,
                    textTransform: 'none',
                    background: `linear-gradient(90deg, ${GOLD} 0%, #feb700 100%)`,
                    color: '#392700',
                    boxShadow: '0 8px 24px rgba(255,201,101,0.2)',
                    '&:hover': { background: 'linear-gradient(90deg, #ffd080 0%, #feb700 100%)' },
                }}>
                {ctaLabel}
            </Button>
            {scheduledLabel ? (
                <Typography sx={{ fontSize: 12, color: MUTED, textAlign: 'center', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    Or wait — your balance transfers automatically on the scheduled date above. No action needed.
                </Typography>
            ) : null}
        </Box>
    );
}

// ── Component ─────────────────────────────────────────────────────────────────
export type CashOutSourceWallet = 'account' | 'campaign' | 'sandbox';

interface CashOutModalProps {
    open: boolean;
    onClose: () => void;
    /** `account` = Earned Revenue. `campaign` = BuzzBridge. `sandbox` = course earnings. */
    sourceWallet?: CashOutSourceWallet;
}

type UnifiedPayoutInfo = Omit<ICampaignPayoutInfo, 'has_active_subscription' | 'message'> &
    Partial<IAccountPayoutInfo> & {
        has_active_subscription?: boolean;
        message?: string | null;
        cashout_tier?: string;
        payout_timing?: string;
        full_balance_only?: boolean;
        has_external_account?: boolean;
        delayed_working_days?: number;
    };

const CashOutModal: React.FC<CashOutModalProps> = ({ open, onClose, sourceWallet = 'campaign' }) => {
    const isAccountWallet = sourceWallet === 'account';
    const isSandboxWallet = sourceWallet === 'sandbox';
    const payoutKind = isAccountWallet ? 'revenue_payout' : isSandboxWallet ? 'sandbox_payout' : 'campaign_payout';
    const dispatch = useDispatch();
    const token = useSelector((s: any) => s.authentication.token as string);
    const { userExternalAccounts, gettingUserExternalAccounts } = useSelector((state: RootState) => state.account);
    const { requestingPayout, payoutRequestError } = useSelector((state: RootState) => state.requests);

    const [payoutInfo, setPayoutInfo] = useState<UnifiedPayoutInfo | null>(null);
    const [fetchingBalance, setFetchingBalance] = useState(false);
    const [payouts, setPayouts] = useState<IPayoutRecord[]>([]);
    const [fetchingPayouts, setFetchingPayouts] = useState(false);
    const [amount, setAmount] = useState(0);
    const [amountInput, setAmountInput] = useState('0');
    const [selectedAccount, setSelectedAccount] = useState('');
    const [tab, setTab] = useState<'request' | 'history'>('request');

    const balanceCents = Math.round(Number(payoutInfo?.balance_cents ?? 0));
    const pendingBalanceCents = Math.round(Number((payoutInfo as any)?.pending_balance_cents ?? 0));
    const availableAmount = balanceCents / 100;
    const pendingAmount = pendingBalanceCents / 100;
    const ohHoldDays = Number((payoutInfo as any)?.office_hours_hold_days ?? 7) || 7;
    const minimumWithdrawal = payoutInfo ? Math.ceil(payoutInfo.minimum_withdrawal_cents / 100) : 0;
    const currency = payoutInfo?.currency ?? 'USD';
    const canRequestNow = Boolean(payoutInfo?.can_request_now && payoutInfo?.withdrawal_enabled);
    const fullBalanceOnly = Boolean(payoutInfo?.full_balance_only);

    /** Always derive request cents from USD amount, capped to real ledger cents. */
    const payoutAmountCents = Math.min(Math.max(0, Math.round(amount * 100)), balanceCents);

    const applyPayoutInfo = useCallback((info: UnifiedPayoutInfo) => {
        setPayoutInfo(info);
        const balUsd = Math.round(info.balance_cents) / 100;
        if (info.full_balance_only) {
            setAmount(balUsd > 0 ? balUsd : 0);
            setAmountInput(balUsd > 0 ? balUsd.toFixed(2) : '0');
            return;
        }
        const minUsd = Math.ceil(info.minimum_withdrawal_cents / 100);
        const initial = Math.min(balUsd, minUsd > 0 ? minUsd : balUsd);
        const rounded = initial > 0 ? Math.round(initial * 100) / 100 : 0;
        setAmount(rounded);
        setAmountInput(rounded > 0 ? rounded.toFixed(2) : '0');
    }, []);

    const fetchPayoutInfo = useCallback(() => {
        setFetchingBalance(true);
        const headers = { Authorization: `Bearer ${token}` };
        if (isAccountWallet) {
            axios
                .get<{ data: any }>(creatorAccountPayoutInfoUrl, { headers })
                .then(res => applyPayoutInfo(normalizeAccountPayoutInfo(res.data?.data ?? res.data)))
                .catch(() => {
                    setPayoutInfo(null);
                    setAmount(0);
                    setAmountInput('0');
                })
                .finally(() => setFetchingBalance(false));
            return;
        }
        if (isSandboxWallet) {
            axios
                .get<{ data: any }>(sandboxAccountPayoutInfoUrl, { headers })
                .then(res => applyPayoutInfo(normalizeAccountPayoutInfo(res.data?.data ?? res.data)))
                .catch(() => {
                    setPayoutInfo(null);
                    setAmount(0);
                    setAmountInput('0');
                })
                .finally(() => setFetchingBalance(false));
            return;
        }
        axios
            .get<{ data: any }>(campaignAccountPayoutInfoUrl, { headers })
            .then(res => applyPayoutInfo(normalizeCampaignPayoutInfo(res.data?.data ?? res.data)))
            .catch(() =>
                axios
                    .get<{ data: any }>(accountBalanceUrl, { headers })
                    .then(res => applyPayoutInfo(normalizeCampaignPayoutInfo(res.data?.data ?? res.data)))
                    .catch(() => {
                        setPayoutInfo(null);
                        setAmount(0);
                        setAmountInput('0');
                    }),
            )
            .finally(() => setFetchingBalance(false));
    }, [token, applyPayoutInfo, isAccountWallet, isSandboxWallet]);

    const fetchPayouts = useCallback(() => {
        setFetchingPayouts(true);
        axios
            .get<{ data: { payouts: IPayoutRecord[] } }>(`${myPayoutsUrl}?page=1&limit=20`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => {
                const rows = res.data.data.payouts ?? [];
                const kind = payoutKind;
                setPayouts(rows.filter(r => r.request_kind === kind || (!r.request_kind && isAccountWallet)));
            })
            .catch(() => setPayouts([]))
            .finally(() => setFetchingPayouts(false));
    }, [token, payoutKind, isAccountWallet]);

    useEffect(() => {
        if (open) {
            dispatch(getUserExternalAccounts());
            setSelectedAccount('');
            setTab('request');
            fetchPayouts();
            fetchPayoutInfo();
        }
    }, [open, dispatch, fetchPayouts, fetchPayoutInfo]);

    useEffect(() => {
        if (!open || !userExternalAccounts?.length) return;
        if (selectedAccount && userExternalAccounts.some(a => accountId(a) === selectedAccount)) return;
        const primary = userExternalAccounts.find(a => a.is_primary);
        const next = accountId(primary ?? userExternalAccounts[0]);
        if (next) setSelectedAccount(next);
    }, [open, userExternalAccounts, selectedAccount]);

    const canSubmit =
        canRequestNow &&
        payoutAmountCents >= Math.round(minimumWithdrawal * 100) &&
        !!selectedAccount &&
        payoutAmountCents > 0 &&
        payoutAmountCents <= balanceCents &&
        (!fullBalanceOnly || payoutAmountCents === balanceCents) &&
        !requestingPayout;

    // Detect successful payout: requestingPayout flips false with no error
    const wasRequestingPayout = useRef(false);
    useEffect(() => {
        if (wasRequestingPayout.current && !requestingPayout && !payoutRequestError) {
            onClose();
            fetchPayouts();
            fetchPayoutInfo();
        }
        wasRequestingPayout.current = requestingPayout;
    }, [requestingPayout, payoutRequestError, onClose, fetchPayouts, fetchPayoutInfo]);

    const scheduledLabel = formatCampaignScheduledPayout(
        payoutInfo?.next_scheduled_payout_at,
        payoutInfo?.next_scheduled_payout_timezone,
    );
    const blockReason = payoutInfo?.block_reason;
    const isScheduledOnlyGate = Boolean(payoutInfo) && !canRequestNow && blockReason === 'awaiting_scheduled_payout';
    const isPlanBlocked = isAccountWallet && blockReason === 'plan_not_eligible';
    const needsExternalAccount =
        Boolean(payoutInfo) &&
        (blockReason === 'missing_external_account' || (isAccountWallet && !userExternalAccounts?.length));

    const eligibilityMessage =
        payoutInfo?.message ||
        (blockReason === 'awaiting_scheduled_payout' && scheduledLabel
            ? `Without an active subscription, payouts run on the scheduled automatic transfer. Next payout: ${scheduledLabel}.`
            : blockReason === 'awaiting_scheduled_payout'
            ? 'Without an active subscription, payouts run on the scheduled automatic transfer. Subscribe to request an early cash out.'
            : blockReason === 'below_minimum'
            ? `Minimum withdrawal is ${formatCurrency(minimumWithdrawal, currency)}.`
            : blockReason === 'pending_payout'
            ? isSandboxWallet
                ? 'You already have a pending Sandbox payout request.'
                : isAccountWallet
                ? 'You already have a pending Earned Revenue payout request.'
                : 'You already have a pending campaign payout request.'
            : blockReason === 'plan_not_eligible'
            ? isSandboxWallet
                ? 'Cash out from Sandbox requires Pro or Pro Plus. Basic plans are not eligible.'
                : 'Cash out from Earned Revenue requires Pro or Pro Plus. Basic plans are not eligible.'
            : blockReason === 'missing_external_account'
            ? 'Add a payout account before you can cash out.'
            : null);

    const processingTimeLabel =
        isAccountWallet || isSandboxWallet
            ? payoutInfo?.payout_timing === 'immediate' || payoutInfo?.cashout_tier === 'instant'
                ? 'Immediate after approval'
                : scheduledLabel
                ? `Up to ${payoutInfo?.delayed_working_days ?? 14} working days · Next: ${scheduledLabel}`
                : `Up to ${payoutInfo?.delayed_working_days ?? 14} working days`
            : '3–7 business days';

    const handleSubscribeForEarlyCashOut = () => {
        onClose();
        dispatch(toggleSubscriptionModal(true));
    };

    const handleAddAccount = () => {
        onClose();
        dispatch(navigate({ routes: ROUTES.ACCOUNT_EXTERNAL_ACCOUNT }));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BG,
                    backgroundImage: 'none',
                    borderRadius: 4,
                    border: `1px solid ${BORDER}`,
                    overflow: 'hidden',
                    m: 2,
                    maxHeight: 'calc(100dvh - 32px)',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {/* Header */}
            <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: WHITE }}>
                        {isSandboxWallet
                            ? 'Cash Out · Sandbox'
                            : isAccountWallet
                            ? 'Cash Out · Earned Revenue'
                            : 'Cash Out'}
                    </Typography>
                    <IconButton onClick={onClose} size="small" sx={{ color: MUTED, '&:hover': { color: WHITE } }}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <Box sx={{ display: 'flex', borderBottom: `1px solid ${BORDER}` }}>
                    {(['request', 'history'] as const).map(t => (
                        <Box
                            key={t}
                            onClick={() => setTab(t)}
                            sx={{
                                flex: 1,
                                textAlign: 'center',
                                pb: 1.25,
                                cursor: 'pointer',
                                borderBottom: tab === t ? `2px solid ${GOLD}` : '2px solid transparent',
                                mb: '-1px',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontFamily: tab === t ? FONT_ENUM.MAIN_BOLD : FONT_ENUM.MAIN_MEDIUM,
                                    color: tab === t ? GOLD : MUTED,
                                }}>
                                {t === 'request' ? 'Request Payout' : 'History'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Request tab */}
            {tab === 'request' && (
                <Box
                    sx={{
                        px: 3,
                        pt: 3,
                        pb: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5,
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    }}>
                    {/* Balance */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            bgcolor: SURFACE,
                            borderRadius: 3,
                            px: 2.5,
                            py: 1.75,
                            border: `1px solid ${BORDER}`,
                        }}>
                        <AccountBalanceWallet sx={{ color: GOLD, fontSize: 22 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: MUTED,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    letterSpacing: '0.1em',
                                }}>
                                AVAILABLE BALANCE
                            </Typography>
                            {fetchingBalance ? (
                                <Skeleton
                                    variant="text"
                                    width={90}
                                    height={32}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1 }}
                                />
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: 20, fontFamily: FONT_ENUM.MAIN_BOLD, color: WHITE }}>
                                        {formatCurrency(availableAmount, currency)}
                                    </Typography>
                                    {isSandboxWallet && pendingAmount > 0 ? (
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: MUTED,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                mt: 0.25,
                                            }}>
                                            {formatCurrency(pendingAmount, currency)} pending (releases {ohHoldDays}d
                                            after each office-hours session)
                                        </Typography>
                                    ) : null}
                                </>
                            )}
                        </Box>
                        {payoutInfo && !canRequestNow && (
                            <Box
                                sx={{
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: 999,
                                    bgcolor: 'rgba(255,181,71,0.1)',
                                    border: '1px solid rgba(255,181,71,0.25)',
                                }}>
                                <Typography sx={{ fontSize: 10, color: AMBER, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    {blockReason === 'awaiting_scheduled_payout'
                                        ? 'Scheduled payout'
                                        : blockReason === 'pending_payout'
                                        ? 'Pending'
                                        : blockReason === 'below_minimum'
                                        ? 'Below minimum'
                                        : blockReason === 'plan_not_eligible'
                                        ? 'Pro required'
                                        : blockReason === 'missing_external_account'
                                        ? 'Add account'
                                        : 'Unavailable'}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {scheduledLabel &&
                    (isAccountWallet
                        ? payoutInfo?.cashout_tier === 'delayed' ||
                          payoutInfo?.payout_timing === 'scheduled' ||
                          Boolean(payoutInfo?.pending_payout)
                        : !isScheduledOnlyGate) ? (
                        <Box
                            sx={{
                                bgcolor: SURFACE,
                                borderRadius: 3,
                                px: 2.5,
                                py: 1.75,
                                border: `1px solid ${BORDER}`,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: MUTED,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    letterSpacing: '0.1em',
                                    mb: 0.5,
                                }}>
                                {isAccountWallet || isSandboxWallet ? 'NEXT PAYOUT DATE' : 'NEXT AUTOMATIC PAYOUT'}
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontFamily: FONT_ENUM.MAIN_BOLD, color: WHITE }}>
                                {scheduledLabel}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: MUTED,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    mt: 0.75,
                                    lineHeight: 1.45,
                                }}>
                                {isAccountWallet
                                    ? `Allow up to ${
                                          payoutInfo?.delayed_working_days ?? 14
                                      } working days. Monday processing may fulfill queued requests.`
                                    : canRequestNow
                                    ? 'You can request an early cash out now. Any remaining balance may still transfer on this schedule.'
                                    : 'Automatic transfers run on this schedule when eligible.'}
                            </Typography>
                        </Box>
                    ) : null}

                    {isPlanBlocked ? (
                        <ScheduledPayoutGate
                            title="Pro or Pro Plus required"
                            message={eligibilityMessage || 'Cash out from Earned Revenue requires Pro or Pro Plus.'}
                            scheduledLabel=""
                            onSubscribe={handleSubscribeForEarlyCashOut}
                            ctaLabel="View Pro plans"
                        />
                    ) : needsExternalAccount ? (
                        <Box
                            sx={{
                                bgcolor: CARD,
                                borderRadius: 3,
                                p: 2.5,
                                border: `1px dashed rgba(255,201,101,0.2)`,
                                textAlign: 'center',
                            }}>
                            <Typography
                                sx={{
                                    color: MUTED,
                                    fontSize: 13,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    mb: 1.5,
                                }}>
                                {eligibilityMessage || 'Add a payout account to cash out.'}
                            </Typography>
                            <Button
                                onClick={handleAddAccount}
                                startIcon={<Add sx={{ fontSize: 16 }} />}
                                sx={{
                                    bgcolor: 'rgba(255,201,101,0.1)',
                                    color: GOLD,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    borderRadius: 999,
                                    textTransform: 'none',
                                    px: 2.5,
                                    '&:hover': { bgcolor: 'rgba(255,201,101,0.18)' },
                                }}>
                                Add account
                            </Button>
                        </Box>
                    ) : isScheduledOnlyGate ? (
                        <ScheduledPayoutGate
                            message={
                                eligibilityMessage ||
                                'Without an active subscription, payouts run on the scheduled automatic transfer.'
                            }
                            scheduledLabel={scheduledLabel}
                            onSubscribe={handleSubscribeForEarlyCashOut}
                        />
                    ) : (
                        <>
                            {/* Amount */}
                            <Box
                                sx={{
                                    bgcolor: CARD,
                                    borderRadius: 3,
                                    px: 2.5,
                                    py: 2.5,
                                    border: `1px solid ${BORDER}`,
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        color: MUTED,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        letterSpacing: '0.1em',
                                        mb: 1,
                                    }}>
                                    WITHDRAWAL AMOUNT
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 2.5 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 28,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: GOLD,
                                            lineHeight: 1,
                                        }}>
                                        {getCurrencySymbol(currency)}
                                    </Typography>
                                    <Box
                                        component="input"
                                        type="text"
                                        inputMode="numeric"
                                        value={amountInput}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            // only allow digits and a single decimal point
                                            if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                                            setAmountInput(raw);
                                            const val = Number(raw);
                                            if (raw !== '' && !isNaN(val)) {
                                                const clamped =
                                                    Math.min(Math.max(Math.round(val * 100), 0), balanceCents) / 100;
                                                setAmount(clamped);
                                            }
                                        }}
                                        onBlur={() => {
                                            const val = Number(amountInput);
                                            if (amountInput !== '' && !isNaN(val)) {
                                                const clamped =
                                                    Math.min(Math.max(Math.round(val * 100), 0), balanceCents) / 100;
                                                setAmount(clamped);
                                                setAmountInput(clamped.toFixed(2));
                                            }
                                        }}
                                        sx={{
                                            background: 'none',
                                            border: 'none',
                                            outline: 'none',
                                            fontSize: 38,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: GOLD,
                                            lineHeight: 1,
                                            width: '100%',
                                            minWidth: 0,
                                            caretColor: GOLD,
                                        }}
                                    />
                                </Box>
                                <Slider
                                    value={amount}
                                    onChange={(_, v) => {
                                        const nextCents = Math.min(
                                            Math.max(Math.round(Number(v) * 100), 0),
                                            balanceCents,
                                        );
                                        const next = nextCents / 100;
                                        setAmount(next);
                                        setAmountInput(next.toFixed(2));
                                    }}
                                    min={minimumWithdrawal}
                                    max={Math.max(availableAmount, minimumWithdrawal)}
                                    step={0.01}
                                    disabled={
                                        fullBalanceOnly ||
                                        !canRequestNow ||
                                        availableAmount <= minimumWithdrawal ||
                                        fetchingBalance
                                    }
                                    sx={{
                                        color: GOLD,
                                        height: 5,
                                        p: 0,
                                        mb: 1,
                                        '& .MuiSlider-track': { bgcolor: GOLD, border: 'none' },
                                        '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.08)' },
                                        '& .MuiSlider-thumb': {
                                            width: 18,
                                            height: 18,
                                            bgcolor: GOLD,
                                            boxShadow: '0 0 0 4px rgba(255,201,101,0.2)',
                                            '&:hover': { boxShadow: '0 0 0 6px rgba(255,201,101,0.25)' },
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 11, color: MUTED }}>
                                        {fullBalanceOnly
                                            ? 'Full balance only'
                                            : `${formatCurrency(minimumWithdrawal, currency)} min`}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: MUTED }}>
                                        {formatCurrency(availableAmount, currency)} max
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Account selection */}
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        color: MUTED,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        letterSpacing: '0.1em',
                                        mb: 1.5,
                                    }}>
                                    PAYOUT TO
                                </Typography>
                                {gettingUserExternalAccounts ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size={24} sx={{ color: GOLD }} />
                                    </Box>
                                ) : !userExternalAccounts?.length ? (
                                    <Box
                                        sx={{
                                            bgcolor: CARD,
                                            borderRadius: 3,
                                            p: 2.5,
                                            border: `1px dashed rgba(255,201,101,0.2)`,
                                            textAlign: 'center',
                                        }}>
                                        <Typography
                                            sx={{
                                                color: MUTED,
                                                fontSize: 13,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                mb: 1.5,
                                            }}>
                                            No bank accounts linked yet
                                        </Typography>
                                        <Button
                                            onClick={handleAddAccount}
                                            startIcon={<Add sx={{ fontSize: 16 }} />}
                                            sx={{
                                                bgcolor: 'rgba(255,201,101,0.1)',
                                                color: GOLD,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 13,
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                px: 2.5,
                                                '&:hover': { bgcolor: 'rgba(255,201,101,0.18)' },
                                            }}>
                                            Add account
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {userExternalAccounts.map(acc => {
                                            const id = accountId(acc);
                                            if (!id) return null;
                                            const sel = selectedAccount === id;
                                            return (
                                                <Box
                                                    key={id}
                                                    role="radio"
                                                    aria-checked={sel}
                                                    onClick={() => setSelectedAccount(id)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        bgcolor: sel ? 'rgba(255,201,101,0.06)' : CARD,
                                                        border: `1px solid ${sel ? 'rgba(255,201,101,0.35)' : BORDER}`,
                                                        borderRadius: 3,
                                                        px: 2,
                                                        py: 1.75,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                        '&:hover': { borderColor: 'rgba(255,201,101,0.25)' },
                                                    }}>
                                                    <Box
                                                        sx={{
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: 2,
                                                            bgcolor: 'rgba(255,201,101,0.08)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}>
                                                        {accountIcon(acc.external_account_type)}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                fontSize: 13,
                                                                color: WHITE,
                                                            }}
                                                            noWrap>
                                                            {acc.name || accountLabel(acc)}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: MUTED }} noWrap>
                                                            {accountLabel(acc)}
                                                            {acc.account_currency ? ` · ${acc.account_currency}` : ''}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            width: 18,
                                                            height: 18,
                                                            borderRadius: '50%',
                                                            flexShrink: 0,
                                                            border: `2px solid ${
                                                                sel ? GOLD : 'rgba(255,255,255,0.25)'
                                                            }`,
                                                            bgcolor: sel ? GOLD : 'transparent',
                                                            boxShadow: sel ? `inset 0 0 0 3px ${CARD}` : 'none',
                                                            transition: 'all 0.15s',
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>

                            {/* Summary */}
                            {selectedAccount && (
                                <Box
                                    sx={{
                                        bgcolor: SURFACE,
                                        borderRadius: 3,
                                        px: 2.5,
                                        py: 2,
                                        border: `1px solid ${BORDER}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}>
                                    {[
                                        { label: 'You receive', value: formatCurrency(amount, 'USD'), bold: true },
                                        { label: 'Processing time', value: processingTimeLabel, bold: false },
                                    ].map(row => (
                                        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: 13, color: MUTED }}>{row.label}</Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: row.bold ? GOLD : WHITE,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {row.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* Eligibility / errors */}
                            {payoutInfo && !canRequestNow && eligibilityMessage ? (
                                <ErrorBanner msg={eligibilityMessage} amber />
                            ) : null}
                            {payoutInfo?.pending_payout?.amount_cents ? (
                                <ErrorBanner
                                    msg={`Pending payout: ${formatCurrency(
                                        (payoutInfo.pending_payout.amount_cents ?? 0) / 100,
                                        currency,
                                    )}`}
                                    amber
                                />
                            ) : null}
                            {payoutRequestError ? <ErrorBanner msg={payoutRequestError} /> : null}

                            {/* Submit — only when withdrawal_enabled / can_request_now */}
                            <Button
                                fullWidth
                                disabled={!canSubmit}
                                onClick={() =>
                                    dispatch(
                                        requestPayout({
                                            request_type: 'payout',
                                            request_kind: payoutKind,
                                            payout_external_account: selectedAccount,
                                            // Integer cents capped to ledger — avoids float overshoot on exact max.
                                            payout_amount: payoutAmountCents,
                                            appName: 'thspian',
                                            metadata: {},
                                        }),
                                    )
                                }
                                sx={{
                                    py: 1.75,
                                    borderRadius: 999,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 15,
                                    textTransform: 'none',
                                    background: canSubmit
                                        ? `linear-gradient(90deg, ${GOLD} 0%, #feb700 100%)`
                                        : 'rgba(255,255,255,0.06)',
                                    color: canSubmit ? '#392700' : MUTED,
                                    boxShadow: canSubmit ? '0 8px 24px rgba(255,201,101,0.2)' : 'none',
                                    '&:hover': canSubmit
                                        ? { background: `linear-gradient(90deg, #ffd080 0%, #feb700 100%)` }
                                        : {},
                                    '&.Mui-disabled': { background: 'rgba(255,255,255,0.06)', color: MUTED },
                                }}>
                                {requestingPayout ? (
                                    <CircularProgress size={22} sx={{ color: '#392700' }} />
                                ) : canRequestNow ? (
                                    'Request Payout'
                                ) : (
                                    'Request unavailable'
                                )}
                            </Button>
                        </>
                    )}
                </Box>
            )}

            {/* History tab */}
            {tab === 'history' && (
                <Box
                    sx={{
                        px: 3,
                        pt: 3,
                        pb: 3,
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    }}>
                    {fetchingPayouts ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                            <CircularProgress size={28} sx={{ color: GOLD }} />
                        </Box>
                    ) : payouts.length === 0 ? (
                        <Box sx={{ textAlign: 'center', pt: 6 }}>
                            <AccountBalanceWallet sx={{ fontSize: 40, color: MUTED, mb: 1.5 }} />
                            <Typography sx={{ fontSize: 14, color: MUTED, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                No payout requests yet
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {payouts.map(req => {
                                const { label, color, bg } = statusMeta(req.request_status);
                                return (
                                    <Box
                                        key={req._id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            bgcolor: CARD,
                                            borderRadius: 3,
                                            px: 2.5,
                                            py: 2,
                                            border: `1px solid ${BORDER}`,
                                        }}>
                                        <Box>
                                            <Typography
                                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: WHITE }}>
                                                {formatCurrency(req.payout_amount / 100, currency)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.25 }}>
                                                {new Date(req.createdAt).toLocaleDateString(undefined, {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ px: 1.5, py: 0.5, borderRadius: 999, bgcolor: bg }}>
                                            <Typography sx={{ fontSize: 11, color, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                                {label}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </Dialog>
    );
};

export { CashOutModal };
